/**
 * src/components/pages/ContactPage.js
 *
 * Página de contacto: formulario + info + mapa.
 */

import '@/styles/contact-page.css'
import { define, c } from '@components/Component.js'
import { h } from '@core/dom.js'
import { signal, effect } from '@core/signal.js'
import { contactData } from '@/db/contact.db.js'

define('ContactPage', () => {
    const { hero, info, form, mapEmbed } = contactData

    /* ── Banner ── */
    const banner = h('div', { className: 'contact-banner' },
        h('div', { className: 'contact-banner__inner' },
            h('span', { className: 'contact-banner__label' }, hero.label),
            h('h1',   { className: 'contact-banner__headline' }, hero.headline),
            h('p',    { className: 'contact-banner__sub' }, hero.sub)
        )
    )

    /* ── Info cards ── */
    const infoCards = info.map(item =>
        h('div', { className: 'contact-info__card' },
            h('span', { className: 'contact-info__icon' }, item.icon),
            h('p',    { className: 'contact-info__title' }, item.title),
            h('div',  { className: 'contact-info__lines' },
                ...item.lines.map(l => h('span', { className: 'contact-info__line' }, l))
            ),
            item.link
                ? h('a', {
                    href:      item.link,
                    target:    item.link.startsWith('http') ? '_blank' : null,
                    rel:       item.link.startsWith('http') ? 'noopener' : null,
                    className: 'contact-info__link'
                }, `${item.linkLabel} →`)
                : null
        )
    )

    const mapEl = h('div', { className: 'contact-map' },
        h('iframe', {
            src:             mapEmbed,
            allowfullscreen: true,
            loading:         'lazy',
            title:           'Ubicación UBIYAAA'
        })
    )

    const infoCol = h('div', { className: 'contact-info' },
        h('div', { className: 'contact-info__grid' }, ...infoCards),
        mapEl
    )

    /* ── Formulario ── */
    const sent = signal(false)

    const nameInput    = h('input',    { type: 'text',  className: 'contact-form__input',    placeholder: 'Tu nombre', required: true })
    const emailInput   = h('input',    { type: 'email', className: 'contact-form__input',    placeholder: 'correo@ejemplo.com', required: true })
    const phoneInput   = h('input',    { type: 'tel',   className: 'contact-form__input',    placeholder: '+57 300 000 0000' })
    const subjectSel   = h('select',   { className: 'contact-form__select', required: true },
        h('option', { value: '', disabled: true, selected: true }, 'Selecciona un tema'),
        ...form.subjects.map(s => h('option', { value: s }, s))
    )
    const msgInput     = h('textarea', { className: 'contact-form__textarea', placeholder: 'Cuéntanos cómo podemos ayudarte...', required: true })
    const submitBtn    = h('button',   { type: 'submit', className: 'contact-form__btn' }, '✉️ Enviar mensaje')

    const formEl = h('form', { className: 'contact-form', novalidate: false },
        h('div', { className: 'contact-form__row' },
            h('div', { className: 'contact-form__field' },
                h('label', { className: 'contact-form__label' }, 'Nombre *'),
                nameInput
            ),
            h('div', { className: 'contact-form__field' },
                h('label', { className: 'contact-form__label' }, 'Correo electrónico *'),
                emailInput
            )
        ),
        h('div', { className: 'contact-form__row' },
            h('div', { className: 'contact-form__field' },
                h('label', { className: 'contact-form__label' }, 'Teléfono'),
                phoneInput
            ),
            h('div', { className: 'contact-form__field' },
                h('label', { className: 'contact-form__label' }, 'Asunto *'),
                subjectSel
            )
        ),
        h('div', { className: 'contact-form__field' },
            h('label', { className: 'contact-form__label' }, 'Mensaje *'),
            msgInput
        ),
        submitBtn
    )

    const successMsg = h('div', { className: 'contact-form__success' },
        h('span', { className: 'contact-form__success-icon' }, '✅'),
        h('h3', {}, '¡Mensaje enviado!'),
        h('p',  {}, 'Gracias por contactarnos. Te responderemos a la mayor brevedad posible.')
    )

    formEl.addEventListener('submit', async e => {
        e.preventDefault()
        if (!formEl.checkValidity()) { formEl.reportValidity(); return }

        submitBtn.disabled   = true
        submitBtn.textContent = '⏳ Enviando...'

        try {
            const res = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    service_id:  'service_m3dtonq',
                    template_id: 'template_4gmcwfa',
                    user_id:     'ns5mXXbWzkaqQy2co',
                    template_params: {
                        name:    nameInput.value.trim(),
                        email:   emailInput.value.trim(),
                        phone:   phoneInput.value.trim() || '—',
                        title:   subjectSel.value,
                        message: msgInput.value.trim(),
                    }
                })
            })

            if (!res.ok) throw new Error(`Error ${res.status}`)
            sent.set(true)
        } catch {
            submitBtn.disabled    = false
            submitBtn.textContent = '✉️ Enviar mensaje'
            alert('No se pudo enviar el mensaje. Por favor inténtalo de nuevo.')
        }
    })

    effect(() => {
        formEl.style.display       = sent.get() ? 'none'  : 'flex'
        successMsg.style.display   = sent.get() ? 'flex'  : 'none'
    })

    const formWrap = h('div', { className: 'contact-form-wrap' },
        h('h2', { className: 'contact-form__title' }, '📬 Envíanos un mensaje'),
        formEl,
        successMsg
    )

    return h('div', { className: 'page page--contact' },
        c('Header'),
        h('div', { className: 'contact-page' },
            banner,
            h('div', { className: 'contact-body' },
                infoCol,
                formWrap
            )
        ),
        c('Footer')
    )
})
